import type {
  PopularityWindow,
  RankHistoryResponse,
  RankMovementResponse,
  ServerFqdn,
} from '../types/starchart';

const API_BASE_URL = 'https://spy.playcdu.co/api/notes/starchart';

/**
 * ARGUS-collected rank history.
 *
 * The upstream StarChart API cannot serve per-server popularity over time — its documented route
 * is shadowed by a catch-all — so argus-backend samples all three ranking windows on its
 * 15-minute cron and serves the history back from D1. Rank *position* is stored alongside the
 * index, which the upstream never recorded at all.
 *
 * Collection is forward-only, so `coverage.earliest` is the true start of the data. Everything
 * here is optional to the module: when it returns nothing the UI says when collection began
 * rather than showing an error.
 */
class StarchartRankHistoryApiService {
  private async request<T>(endpoint: string, signal?: AbortSignal): Promise<T> {
    // Cloudflare Access cookies ride along automatically; no explicit auth headers needed.
    const response = await fetch(`${API_BASE_URL}${endpoint}`, { signal });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Rank history API Error: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  private buildQueryString(
    params: Record<string, string | number | undefined | null>,
  ): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  /** Popularity index and rank position over time for one server. */
  async getRankHistory(
    serverId: ServerFqdn,
    window: PopularityWindow,
    start: Date,
    end: Date,
    signal?: AbortSignal,
  ): Promise<RankHistoryResponse> {
    const qs = this.buildQueryString({
      server_id: serverId,
      window,
      start: Math.floor(start.getTime() / 1000),
      end: Math.floor(end.getTime() / 1000),
    });
    return this.request<RankHistoryResponse>(`/rank-history${qs}`, signal);
  }

  /** Every server's current position in a window against an earlier snapshot. */
  async getRankMovement(
    window: PopularityWindow,
    sinceSeconds = 24 * 60 * 60,
    signal?: AbortSignal,
  ): Promise<RankMovementResponse> {
    const qs = this.buildQueryString({ window, since_seconds: sinceSeconds });
    return this.request<RankMovementResponse>(`/rank-movement${qs}`, signal);
  }
}

export const starchartRankHistoryApiService = new StarchartRankHistoryApiService();
